import React, { useMemo, useState } from "react";
import {
  Button,
  Calendar,
  Card,
  Col,
  Collapse,
  Modal,
  Popconfirm,
  Row,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  getDayCalendarStats,
  getDayTransactions,
  getDayReturnCount,
  getDayReturns,
  getParentStatusColor,
  getParentStatusLabel,
  formatDateTime,
  formatAmount,
  addTextColumnTools,
  addNumberColumnTools,
  addDateColumnTools,
} from "./transactionScheduleUi";
import { ReturnActionCell } from "./UnmatchedReturnsTab";
import TableMetaBar from "../common/TableMetaBar";

const { Text } = Typography;

function groupAmountsByCurrency(rows = [], amountField = "paymentAmount", currencyField = "currency") {
  const map = new Map();

  rows.forEach((item) => {
    const currency = item?.[currencyField] || "-";
    const amount = Number(item?.[amountField] || 0);

    if (!Number.isFinite(amount)) return;

    map.set(currency, (map.get(currency) || 0) + amount);
  });

  return Array.from(map.entries()).map(([currency, amount]) => ({
    currency,
    amount,
  }));
}

function renderCurrencyTotals(totals = [], emptyText = "-") {
  if (!totals.length) {
    return <Text type="secondary">{emptyText}</Text>;
  }

  return (
    <Space size={[6, 6]} wrap>
      {totals.map((item) => (
        <Tag key={item.currency} color="blue" style={{ marginInlineEnd: 0 }}>
          {item.currency}: {formatAmount(item.amount)}
        </Tag>
      ))}
    </Space>
  );
}

function getSchedulePriority(record) {
  const total = Number(record.allocationCount || 0);
  const agreed = Number(record.agreedAllocationCount || 0);
  const returned = Number(record.returnedAllocationCount || 0);
  const review = Number(record.reviewAllocationCount || 0);
  const verified = Number(record.verifiedAllocationCount || 0);

  const fullyAgreed = total > 0 && agreed === total;
  const partiallyAgreed = total > 0 && agreed < total;

  if (partiallyAgreed) return 0;
  if (returned > 0) return 1;
  if (review > 0) return 2;
  if (record.parentDisplayStatus === "EMPTY") return 3;
  if (fullyAgreed || verified > 0) return 4;
  return 5;
}

function getRowClassName(record) {
  const total = Number(record.allocationCount || 0);
  const agreed = Number(record.agreedAllocationCount || 0);
  const returned = Number(record.returnedAllocationCount || 0);
  const review = Number(record.reviewAllocationCount || 0);
  const verified = Number(record.verifiedAllocationCount || 0);

  const fullyAgreed = total > 0 && agreed === total;
  const partiallyAgreed = total > 0 && agreed < total;

  if (partiallyAgreed) return "ts-row-unagreed";
  if (returned > 0) return "ts-row-returned";
  if (review > 0) return "ts-row-review";
  if (record.parentDisplayStatus === "EMPTY") return "ts-row-empty";
  if (fullyAgreed || verified > 0) return "ts-row-verified";

  return "";
}

function TransactionScheduleCalendarCard({
  filteredTransactions,
  returns,
  returnHistory,
  runAllChecksButton,
  onOpenRecord,
  onManageRecord,
  canDelete,
  onDeleteRecord,
  selectedRecord,
  refreshDetails,
  loadTransactions,
  loadUnmatchedReturns,
  loadIgnoredReturns,
  refreshReturnHistory,
  onViewInvoice,
  onDownloadInvoice,
  onViewContract,
  onDownloadContract,
}) {
  const [calendarSelectedDate, setCalendarSelectedDate] = useState(dayjs());
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [calendarDayRecords, setCalendarDayRecords] = useState([]);
  const [calendarDayReturns, setCalendarDayReturns] = useState([]);
  const [calendarDayReturnHistory, setCalendarDayReturnHistory] = useState([]);
  const [calendarActiveTab, setCalendarActiveTab] = useState("schedules");

  const [calendarSchedulesMeta, setCalendarSchedulesMeta] = useState({
    filteredCount: null,
    filters: {},
    sorter: {},
  });

  const [calendarReturnsMeta, setCalendarReturnsMeta] = useState({
    filteredCount: null,
    filters: {},
    sorter: {},
  });

  const [calendarHistoryMeta, setCalendarHistoryMeta] = useState({
    filteredCount: null,
    filters: {},
    sorter: {},
  });

  const renderCalendarHeader = ({ value, onChange }) => {
    const current = value || calendarSelectedDate;

    const goPrevMonth = () => {
      const next = current.clone().subtract(1, "month");
      setCalendarSelectedDate(next);
      onChange(next);
    };

    const goNextMonth = () => {
      const next = current.clone().add(1, "month");
      setCalendarSelectedDate(next);
      onChange(next);
    };

    const goToday = () => {
      const next = dayjs();
      setCalendarSelectedDate(next);
      onChange(next);
    };

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <Space size="small">
          <Button size="small" icon={<LeftOutlined />} onClick={goPrevMonth} />
          <Button size="small" icon={<RightOutlined />} onClick={goNextMonth} />
          <Button size="small" onClick={goToday}>
            Өнөөдөр
          </Button>
        </Space>

        <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>
          {current.format("YYYY-MM")}
        </div>
      </div>
    );
  };

  const getDayReturnHistory = (history, date) => {
    return (history || []).filter(
      (item) =>
        item.transactionDate &&
        dayjs(item.transactionDate).format("YYYY-MM-DD") === dayjs(date).format("YYYY-MM-DD"),
    );
  };

  const getDayReturnHistoryCount = (history, date) => {
    return getDayReturnHistory(history, date).length;
  };

  const refreshCalendarModalData = async (dateValue = calendarSelectedDate) => {
    const [transactionsResult, returnsResult, historyResult] = await Promise.all([
      loadTransactions?.(),
      loadUnmatchedReturns?.(),
      refreshReturnHistory?.(),
    ]);

    const nextTransactions = Array.isArray(transactionsResult) ? transactionsResult : filteredTransactions;
    const nextReturns = Array.isArray(returnsResult) ? returnsResult : returns;
    const nextReturnHistory = Array.isArray(historyResult) ? historyResult : returnHistory;

    const rows = getDayTransactions(nextTransactions, dateValue)
      .map((item) => ({ ...item, key: item.id }))
      .sort((a, b) => {
        const p = getSchedulePriority(a) - getSchedulePriority(b);
        if (p !== 0) return p;
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      });

    const returnsForDay = getDayReturns(nextReturns, dateValue).map((item) => ({
      ...item,
      key: item.statementId,
    }));

    const historyForDay = getDayReturnHistory(nextReturnHistory, dateValue).map((item, index) => ({
      ...item,
      key: `${item.statementId}-${item.allocationId || "x"}-${item.confirmedAt || index}`,
    }));

    setCalendarDayRecords(rows);
    setCalendarDayReturns(returnsForDay);
    setCalendarDayReturnHistory(historyForDay);
  };

  const monthSummary = useMemo(() => {
    const currentMonth = calendarSelectedDate.format("YYYY-MM");

    const monthTransactions = (filteredTransactions || []).filter(
      (item) => item.createdAt && dayjs(item.createdAt).format("YYYY-MM") === currentMonth,
    );

    const monthReturns = (returns || []).filter(
      (item) => item.transactionDate && dayjs(item.transactionDate).format("YYYY-MM") === currentMonth,
    );

    const monthReturnHistory = (returnHistory || []).filter(
      (item) => item.transactionDate && dayjs(item.transactionDate).format("YYYY-MM") === currentMonth,
    );

    const totalSchedules = monthTransactions.length;
    const totalsByCurrency = groupAmountsByCurrency(monthTransactions, "paymentAmount", "currency");

    const unagreed = monthTransactions.filter((x) => {
      const totalAlloc = Number(x.allocationCount || 0);
      const agreed = Number(x.agreedAllocationCount || 0);
      return totalAlloc > 0 && agreed < totalAlloc;
    }).length;

    const review = monthTransactions.reduce((sum, x) => sum + Number(x.reviewAllocationCount || 0), 0);
    const returned = monthTransactions.reduce((sum, x) => sum + Number(x.returnedAllocationCount || 0), 0);

    return {
      totalSchedules,
      totalsByCurrency,
      unagreed,
      review,
      returned,
      openReturns: monthReturns.length,
      historyReturns: monthReturnHistory.length,
    };
  }, [calendarSelectedDate, filteredTransactions, returns, returnHistory]);

  const dateCellRender = (value) => {
    const stats = getDayCalendarStats(filteredTransactions, value);
    const openReturnCount = getDayReturnCount(returns, value);
    const confirmedReturnCount = getDayReturnHistoryCount(returnHistory, value);
    const dayRows = getDayTransactions(filteredTransactions, value);
    const totalsByCurrency = groupAmountsByCurrency(dayRows, "paymentAmount", "currency");

    const hasAnyData = stats.total > 0 || openReturnCount > 0 || confirmedReturnCount > 0;

    if (!hasAnyData) {
      return <div style={{ minHeight: 72 }} />;
    }

    const attentionCount = stats.unagreed + stats.review + openReturnCount;
    const isAttentionDay = attentionCount > 0;
    const isReturnDay = openReturnCount > 0;

    return (
      <div
        style={{
          minHeight: 72,
          borderRadius: 10,
          padding: 6,
          background: isAttentionDay ? (isReturnDay ? "#fff1f0" : "#fff7e6") : "#fafafa",
          border: isAttentionDay
            ? isReturnDay
              ? "1px solid #ff7875"
              : "1px solid #ffd591"
            : "1px solid #f0f0f0",
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 3 }}>
          Нийт: {stats.total}
        </div>

        <div className="flex flex-wrap gap-1" style={{ marginBottom: 4 }}>
          {stats.returned > 0 ? <Tag color="red">R {stats.returned}</Tag> : null}
          {stats.unagreed > 0 ? <Tag color="orange">U {stats.unagreed}</Tag> : null}
          {stats.review > 0 ? <Tag color="magenta">RV {stats.review}</Tag> : null}
          {openReturnCount > 0 ? <Tag color="red">RET {openReturnCount}</Tag> : null}
          {confirmedReturnCount > 0 ? <Tag color="cyan">HIS {confirmedReturnCount}</Tag> : null}
          {stats.agreed > 0 ? <Tag color="green">A {stats.agreed}</Tag> : null}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {totalsByCurrency.slice(0, 1).map((item) => (
            <div key={item.currency} style={{ fontSize: 11, color: "#666", fontWeight: 600 }}>
              {item.currency}: {formatAmount(item.amount)}
            </div>
          ))}
          {totalsByCurrency.length > 1 ? (
            <div style={{ fontSize: 11, color: "#888" }}>+{totalsByCurrency.length - 1} валют</div>
          ) : null}
        </div>
      </div>
    );
  };

  const handleCalendarSelect = (value) => {
    setCalendarSelectedDate(value);

    const rows = getDayTransactions(filteredTransactions, value)
      .map((item) => ({ ...item, key: item.id }))
      .sort((a, b) => {
        const p = getSchedulePriority(a) - getSchedulePriority(b);
        if (p !== 0) return p;
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      });

    const returnsForDay = getDayReturns(returns, value).map((item) => ({
      ...item,
      key: item.statementId,
    }));

    const historyForDay = getDayReturnHistory(returnHistory, value).map((item, index) => ({
      ...item,
      key: `${item.statementId}-${item.allocationId || "x"}-${item.confirmedAt || index}`,
    }));

    const stats = getDayCalendarStats(filteredTransactions, value);
    const openReturnCount = getDayReturnCount(returns, value);
    const hasAnyData = rows.length > 0 || returnsForDay.length > 0 || historyForDay.length > 0;

    if (!hasAnyData) return;

    setCalendarDayRecords(rows);
    setCalendarDayReturns(returnsForDay);
    setCalendarDayReturnHistory(historyForDay);
    setCalendarActiveTab(openReturnCount > 0 ? "returns" : "schedules");
    setCalendarModalOpen(true);
  };

  const handleCalendarSchedulesChange = (pagination, filters, sorter, extra) => {
    setCalendarSchedulesMeta({
      filteredCount: Array.isArray(extra?.currentDataSource) ? extra.currentDataSource.length : null,
      filters: filters || {},
      sorter: sorter || {},
    });
  };

  const handleCalendarReturnsChange = (pagination, filters, sorter, extra) => {
    setCalendarReturnsMeta({
      filteredCount: Array.isArray(extra?.currentDataSource) ? extra.currentDataSource.length : null,
      filters: filters || {},
      sorter: sorter || {},
    });
  };

  const handleCalendarHistoryChange = (pagination, filters, sorter, extra) => {
    setCalendarHistoryMeta({
      filteredCount: Array.isArray(extra?.currentDataSource) ? extra.currentDataSource.length : null,
      filters: filters || {},
      sorter: sorter || {},
    });
  };

  const calendarDayTotalsByCurrency = useMemo(() => {
    return groupAmountsByCurrency(calendarDayRecords, "paymentAmount", "currency");
  }, [calendarDayRecords]);

  const calendarDetailColumns = [
    {
      title: "Хүсэлтийн дугаар",
      dataIndex: "requestNumber",
      key: "requestNumber",
      width: 160,
    },
    {
      title: "Төлөгч",
      dataIndex: "payer",
      key: "payer",
      width: 180,
    },
    {
      title: "Хүлээн авагч байгууллага",
      dataIndex: "beneficiaryCompany",
      key: "beneficiaryCompany",
      width: 220,
    },
    {
      title: "Дүн",
      dataIndex: "paymentAmount",
      key: "paymentAmount",
      width: 160,
      render: (value, record) => `${formatAmount(value)} ${record.currency || ""}`.trim(),
    },
    {
      title: "Төлөв",
      dataIndex: "parentDisplayStatus",
      key: "parentDisplayStatus",
      width: 170,
      render: (value, record) => {
        const total = Number(record.allocationCount || 0);
        const agreed = Number(record.agreedAllocationCount || 0);
        const hasUnagreed = total > 0 && agreed < total;

        return (
          <Space size={6}>
            <Tag color={getParentStatusColor(value)}>
              {getParentStatusLabel(value)}
            </Tag>
            {hasUnagreed ? (
              <Tag color="orange" style={{ marginInlineEnd: 0 }}>
                Зөвшөөрөл дутуу
              </Tag>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: "Хавсралт",
      key: "attachments",
      width: 220,
      render: (_, record) => (
        <Space size={6} wrap>
          <Tooltip title="Нэхэмжлэх харах">
            <Button
              size="small"
              icon={<EyeOutlined />}
              disabled={!record.id}
              onClick={() => onViewInvoice?.(record)}
            />
          </Tooltip>

          <Tooltip title="Нэхэмжлэх татах">
            <Button
              size="small"
              icon={<DownloadOutlined />}
              disabled={!record.id}
              onClick={() => onDownloadInvoice?.(record)}
            />
          </Tooltip>

          <Tooltip title="Гэрээ харах">
            <Button
              size="small"
              icon={<EyeOutlined />}
              disabled={!record.id}
              onClick={() => onViewContract?.(record)}
            />
          </Tooltip>

          <Tooltip title="Гэрээ татах">
            <Button
              size="small"
              icon={<DownloadOutlined />}
              disabled={!record.id}
              onClick={() => onDownloadContract?.(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Үйлдэл",
      key: "action",
      width: 170,
      fixed: "right",
      render: (_, record) => {
        const agreed = Number(record.agreedAllocationCount || 0);
        const canDeleteRow = canDelete && agreed === 0;
        const isEmpty = record.parentDisplayStatus === "EMPTY";

        return (
          <Space size={6}>
            <Tooltip title="Хуваарь удирдах">
              <Button
                icon={<EditOutlined />}
                type={isEmpty ? "primary" : "default"}
                ghost={isEmpty}
                onClick={() => {
                  setCalendarModalOpen(false);
                  onManageRecord(record);
                }}
              />
            </Tooltip>

            <Tooltip title="Дэлгэрэнгүй">
              <Button
                icon={<EyeOutlined />}
                onClick={() => {
                  setCalendarModalOpen(false);
                  onOpenRecord(record);
                }}
              />
            </Tooltip>

            {canDelete ? (
              <Tooltip
                title={
                  canDeleteRow ? "Зөвхөн зөвшөөрөгдөөгүй мөрийг устгана" : "Зөвхөн зөвшөөрөгдөөгүй мөрийг устгана"
                }
              >
                <span>
                  <Popconfirm
                    title="Гүйлгээний хуваарь устгах уу?"
                    description="Зөвхөн зөвшөөрөгдөөгүй хуваарийг устгах боломжтой."
                    okText="Устгах"
                    cancelText="Болих"
                    okButtonProps={{ danger: true }}
                    disabled={!canDeleteRow}
                    onConfirm={() => {
                      setCalendarModalOpen(false);
                      onDeleteRecord(record);
                    }}
                  >
                    <Button icon={<DeleteOutlined />} danger disabled={!canDeleteRow} />
                  </Popconfirm>
                </span>
              </Tooltip>
            ) : null}
          </Space>
        );
      },
    },
  ];

  const enhancedCalendarDetailColumns = calendarDetailColumns.map((col) => {
    switch (col.key) {
      case "requestNumber":
        return addTextColumnTools(col, calendarDayRecords, "requestNumber");
      case "payer":
        return addTextColumnTools(col, calendarDayRecords, "payer");
      case "beneficiaryCompany":
        return addTextColumnTools(col, calendarDayRecords, "beneficiaryCompany");
      case "paymentAmount":
        return addNumberColumnTools(col, "paymentAmount");
      default:
        return col;
    }
  });

  const returnColumns = [
    {
      title: "Орсон огноо",
      dataIndex: "transactionDate",
      key: "transactionDate",
      width: 170,
      render: (value) => formatDateTime(value),
    },
    {
      title: "Байгууллага",
      dataIndex: "institutionName",
      key: "institutionName",
      width: 180,
    },
    {
      title: "Хүлээн авсан данс",
      dataIndex: "receivingAccountNumber",
      key: "receivingAccountNumber",
      width: 180,
    },
    {
      title: "Орлого",
      key: "income",
      width: 140,
      render: (_, record) => `${formatAmount(record.income)} ${record.currency || ""}`.trim(),
    },
    {
      title: "Шилжүүлэгчийн нэр",
      dataIndex: "senderName",
      key: "senderName",
      width: 220,
    },
    {
      title: "Утга",
      dataIndex: "description",
      key: "description",
      width: 260,
    },
    {
      title: "Буцаалт батлах",
      key: "confirmReturn",
      width: 560,
      render: (_, record) => (
        <ReturnActionCell
          record={record}
          selectedRecord={selectedRecord}
          refreshDetails={refreshDetails}
          loadTransactions={loadTransactions}
          loadUnmatchedReturns={loadUnmatchedReturns}
          loadIgnoredReturns={loadIgnoredReturns}
          onConfirmed={() => refreshCalendarModalData(calendarSelectedDate)}
        />
      ),
    },
  ];

  const enhancedReturnColumns = returnColumns.map((col) => {
    switch (col.key) {
      case "transactionDate":
        return addDateColumnTools(col, "transactionDate", "descend");
      case "institutionName":
        return addTextColumnTools(col, calendarDayReturns, "institutionName");
      case "receivingAccountNumber":
        return addTextColumnTools(col, calendarDayReturns, "receivingAccountNumber");
      case "income":
        return { ...col, sorter: (a, b) => Number(a.income || 0) - Number(b.income || 0) };
      case "senderName":
        return addTextColumnTools(col, calendarDayReturns, "senderName");
      case "description":
        return addTextColumnTools(col, calendarDayReturns, "description");
      default:
        return col;
    }
  });

  const returnHistoryColumns = [
    {
      title: "Баталгаажуулсан огноо",
      dataIndex: "confirmedAt",
      key: "confirmedAt",
      width: 180,
      render: (value) => formatDateTime(value),
    },
    {
      title: "Баталгаажуулсан хэрэглэгч",
      dataIndex: "confirmedBy",
      key: "confirmedBy",
      width: 180,
      render: (value) => value || "-",
    },
    {
      title: "Орсон огноо",
      dataIndex: "transactionDate",
      key: "transactionDate",
      width: 170,
      render: (value) => formatDateTime(value),
    },
    {
      title: "Байгууллага",
      dataIndex: "institutionName",
      key: "institutionName",
      width: 180,
    },
    {
      title: "Хүлээн авсан данс",
      dataIndex: "receivingAccountNumber",
      key: "receivingAccountNumber",
      width: 180,
    },
    {
      title: "Орлого",
      key: "income",
      width: 140,
      render: (_, record) => `${formatAmount(record.income)} ${record.currency || ""}`.trim(),
    },
    {
      title: "Хүсэлтийн дугаар",
      dataIndex: "requestNumber",
      key: "requestNumber",
      width: 160,
    },
    {
      title: "Төлөгч",
      dataIndex: "payer",
      key: "payer",
      width: 180,
    },
    {
      title: "Хуваарилсан дүн",
      key: "allocatedAmount",
      width: 160,
      render: (_, record) => `${formatAmount(record.allocatedAmount)} ${record.currency || ""}`.trim(),
    },
    {
      title: "Шилжүүлэгчийн нэр",
      dataIndex: "senderName",
      key: "senderName",
      width: 220,
    },
    {
      title: "Утга",
      dataIndex: "description",
      key: "description",
      width: 260,
    },
  ];

  const enhancedReturnHistoryColumns = returnHistoryColumns.map((col) => {
    switch (col.key) {
      case "confirmedAt":
        return addDateColumnTools(col, "confirmedAt", "descend");
      case "confirmedBy":
        return addTextColumnTools(col, calendarDayReturnHistory, "confirmedBy");
      case "transactionDate":
        return addDateColumnTools(col, "transactionDate");
      case "institutionName":
        return addTextColumnTools(col, calendarDayReturnHistory, "institutionName");
      case "receivingAccountNumber":
        return addTextColumnTools(col, calendarDayReturnHistory, "receivingAccountNumber");
      case "income":
        return { ...col, sorter: (a, b) => Number(a.income || 0) - Number(b.income || 0) };
      case "requestNumber":
        return addTextColumnTools(col, calendarDayReturnHistory, "requestNumber");
      case "payer":
        return addTextColumnTools(col, calendarDayReturnHistory, "payer");
      case "allocatedAmount":
        return { ...col, sorter: (a, b) => Number(a.allocatedAmount || 0) - Number(b.allocatedAmount || 0) };
      case "senderName":
        return addTextColumnTools(col, calendarDayReturnHistory, "senderName");
      case "description":
        return addTextColumnTools(col, calendarDayReturnHistory, "description");
      default:
        return col;
    }
  });

  const collapseLabel = useMemo(() => {
    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "center",
          paddingRight: 8,
        }}
      >
        <span style={{ fontWeight: 700 }}>
          {calendarSelectedDate.format("YYYY-MM")} календарь
        </span>
        <span style={{ color: "#666" }}>
          {monthSummary.totalSchedules} хуваарь
        </span>
        <span style={{ color: monthSummary.openReturns > 0 ? "#cf1322" : "#666" }}>
          {monthSummary.openReturns} буцаалт
        </span>
        <span style={{ color: "#666" }}>
          U+RV: {monthSummary.unagreed + monthSummary.review}
        </span>
      </div>
    );
  }, [calendarSelectedDate, monthSummary]);

  return (
    <>
      <Card
        title="Өдрийн гүйлгээ, буцаалт, түүхийн календарь"
        extra={runAllChecksButton}
        bodyStyle={{ paddingTop: 16, paddingBottom: 16 }}
      >
        <Collapse
          defaultActiveKey={[]}
          items={[
            {
              key: "calendar",
              label: collapseLabel,
              children: (
                <>
                  <Row gutter={[10, 10]} style={{ marginBottom: 12 }}>
                    <Col xs={12} md={6}>
                      <Card size="small" bodyStyle={{ padding: 12 }}>
                        <Statistic title="Хуваарь" value={monthSummary.totalSchedules} />
                      </Card>
                    </Col>
                    <Col xs={12} md={6}>
                      <Card size="small" bodyStyle={{ padding: 12 }}>
                        <Statistic title="U + RV" value={monthSummary.unagreed + monthSummary.review} />
                      </Card>
                    </Col>
                    <Col xs={12} md={6}>
                      <Card size="small" bodyStyle={{ padding: 12 }}>
                        <Statistic title="RET" value={monthSummary.openReturns} />
                      </Card>
                    </Col>
                    <Col xs={24} md={6}>
                      <Card size="small" bodyStyle={{ padding: 12 }}>
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>Нийт дүн</div>
                        {renderCurrencyTotals(monthSummary.totalsByCurrency)}
                      </Card>
                    </Col>
                  </Row>

                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      marginBottom: 12,
                      alignItems: "center",
                    }}
                  >
                    <Tag color="orange" style={{ marginInlineEnd: 0 }}>U</Tag>
                    <Tag color="magenta" style={{ marginInlineEnd: 0 }}>RV</Tag>
                    <Tag color="red" style={{ marginInlineEnd: 0 }}>RET</Tag>
                    <Tag color="cyan" style={{ marginInlineEnd: 0 }}>HIS</Tag>
                    <span style={{ fontSize: 12, color: "#888" }}>
                      U: Зөвшөөрөөгүй, RV: Гараар шалгах, RET: Нээлттэй буцаалт, HIS: Түүх
                    </span>
                  </div>

                  <Calendar
                    value={calendarSelectedDate}
                    onSelect={handleCalendarSelect}
                    headerRender={renderCalendarHeader}
                    cellRender={(current, info) => {
                      if (info.type === "date") {
                        return dateCellRender(current);
                      }
                      return info.originNode;
                    }}
                  />
                </>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={`${calendarSelectedDate ? dayjs(calendarSelectedDate).format("YYYY-MM-DD") : ""} өдрийн дэлгэрэнгүй`}
        open={calendarModalOpen}
        onCancel={() => setCalendarModalOpen(false)}
        footer={null}
        width={1500}
      >
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={6}>
            <Card size="small">
              <Statistic title="Хуваарь" value={calendarDayRecords.length} />
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card size="small">
              <Statistic title="Нээлттэй буцаалт" value={calendarDayReturns.length} />
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card size="small">
              <Statistic title="Буцаалтын түүх" value={calendarDayReturnHistory.length} />
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card size="small">
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Өдрийн нийт дүн</div>
              {renderCurrencyTotals(calendarDayTotalsByCurrency)}
            </Card>
          </Col>
        </Row>

        <Tabs
          activeKey={calendarActiveTab}
          onChange={setCalendarActiveTab}
          items={[
            {
              key: "schedules",
              label: `Хуваарь (${calendarDayRecords.length})`,
              children: (
                <>
                  <TableMetaBar
                    totalCount={calendarDayRecords.length}
                    visibleCount={calendarSchedulesMeta.filteredCount}
                    filters={calendarSchedulesMeta.filters}
                    sorter={calendarSchedulesMeta.sorter}
                  />
                  <Table
                    rowKey="id"
                    rowClassName={getRowClassName}
                    columns={enhancedCalendarDetailColumns}
                    sortDirections={["ascend", "descend"]}
                    dataSource={calendarDayRecords}
                    pagination={{ pageSize: 8, pageSizeOptions: [5, 8, 10], showSizeChanger: true }}
                    scroll={{ x: 1200 }}
                    bordered
                    onChange={handleCalendarSchedulesChange}
                  />
                </>
              ),
            },
            {
              key: "returns",
              label: `Нээлттэй буцаалт (${calendarDayReturns.length})`,
              children: (
                <>
                  <TableMetaBar
                    totalCount={calendarDayReturns.length}
                    visibleCount={calendarReturnsMeta.filteredCount}
                    filters={calendarReturnsMeta.filters}
                    sorter={calendarReturnsMeta.sorter}
                  />
                  <Table
                    rowKey="statementId"
                    columns={enhancedReturnColumns}
                    sortDirections={["ascend", "descend"]}
                    dataSource={calendarDayReturns}
                    pagination={{ pageSize: 5 }}
                    scroll={{ x: 1800 }}
                    bordered
                    onChange={handleCalendarReturnsChange}
                  />
                </>
              ),
            },
            {
              key: "history",
              label: `Буцаалтын түүх (${calendarDayReturnHistory.length})`,
              children: (
                <>
                  <TableMetaBar
                    totalCount={calendarDayReturnHistory.length}
                    visibleCount={calendarHistoryMeta.filteredCount}
                    filters={calendarHistoryMeta.filters}
                    sorter={calendarHistoryMeta.sorter}
                  />
                  <Table
                    rowKey={(row) => `${row.statementId}-${row.allocationId || "x"}-${row.confirmedAt || ""}`}
                    columns={enhancedReturnHistoryColumns}
                    sortDirections={["ascend", "descend"]}
                    dataSource={calendarDayReturnHistory}
                    pagination={{ pageSize: 5 }}
                    scroll={{ x: 2200 }}
                    bordered
                    onChange={handleCalendarHistoryChange}
                  />
                </>
              ),
            },
          ]}
        />
      </Modal>
    </>
  );
}

export default TransactionScheduleCalendarCard;